import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { platformOpen } from "@/lib/platformOpen";

// useWhatsAppLink — self-serve WhatsApp account linking.
//
// State comes from whatsapp_links (owner RLS: select/update own row).
// activate() mints a short-lived code via the whatsapp-link-code edge fn and
// opens the wa.me deeplink (pre-filled "VINCULAR-<code>") through the app's
// standard external-open path; the user sends it and whatsapp-webhook writes
// the link. disconnect() soft-unlinks (active=false) — the webhook lookup
// already filters active=true, so no code is needed to reconnect later.
//
// ── LEITURA DE STATUS COM RETRY (issue #461, P2) ────────────────────────────
// A versão anterior lia UMA vez na montagem e tinha dois buracos, os dois
// atingidos pelo steal-cascade do lock de auth na rajada do /mirror:
//   1. `getUser()` com erro era tratado como "deslogado" → `connected=false`
//      DEFINITIVO. Usuário VINCULADO renderizava como não-vinculado.
//   2. O `void refresh()` não tinha catch — um throw (o AbortError cru do
//      cascade) virava unhandled rejection: a cara da VYV-FRONTEND-4.
// Agora: erro ≠ resposta; a montagem tenta com backoff e só desiste avisando.

const REFRESH_RETRY_DELAYS_MS = [0, 1000, 3000, 9000];

export interface UseWhatsAppLinkResult {
  connected: boolean;
  statusKnown?: boolean;
  loading: boolean; // initial status load
  activating: boolean; // mint + open deeplink in flight
  /**
   * Abre o WhatsApp. `source` nomeia a superfície que pediu (ex.:
   * "mirror_soft_ask", "settings_connections") e viaja até o evento
   * `wa_link_code_generated` — sem isso a meta 15,5%→40% não tem direção.
   */
  activate: (source: string) => Promise<void>;
  disconnect: () => Promise<void>;
  /** Uma tentativa de leitura. `true` = resposta DEFINITIVA (mesmo "não vinculado"). */
  refresh: () => Promise<boolean>;
}

export function useWhatsAppLink(): UseWhatsAppLinkResult {
  const [connected, setConnected] = useState(false);
  const [statusKnown, setStatusKnown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);

  const refresh = useCallback(async (): Promise<boolean> => {
    try {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) {
        // Falha de leitura NÃO é "deslogado". `warn` e não `error`: tentativa
        // intermediária é esperada sob contenção; quem carrega o fato é o
        // error terminal do laço de retry.
        setStatusKnown(false);
        console.warn("[useWhatsAppLink] getUser failed", userError);
        return false;
      }
      if (!userData.user) {
        setConnected(false);
        setStatusKnown(true);
        return true;
      }
      // LOTE 4.1 (05/09/2026) — DUAS LINHAS SÃO NORMAIS. whatsapp_links é
      // única por wa_phone (UNIQUE na coluna), não por user_id: quem vinculou
      // de dois números, ou trocou de número e vinculou de novo sem
      // desconectar, tem duas linhas ativas — o webhook faz upsert por
      // wa_phone e nunca desativa a antiga. `.maybeSingle()` sobre
      // user_id + active=true devolvia PGRST116 ("Results contain 2 rows,
      // requires 1 row"), tratado como falha de leitura → 4 tentativas
      // esgotadas → "não vinculado SEM certeza" para um usuário VINCULADO, no
      // canal que mais retém. Vinculado = existe PELO MENOS UMA linha ativa;
      // quantas, não importa aqui. Sem .single()/.maybeSingle() (pinado).
      const { data, error } = await supabase
        .from("whatsapp_links")
        .select("active")
        .eq("user_id", userData.user.id)
        .eq("active", true)
        .limit(1);
      if (error) {
        setStatusKnown(false);
        console.warn("[useWhatsAppLink] status lookup failed", error);
        return false;
      }
      setConnected(Array.isArray(data) && data.length > 0);
      setStatusKnown(true);
      return true;
    } catch (err) {
      setStatusKnown(false);
      console.warn("[useWhatsAppLink] refresh threw", err);
      return false;
    } finally {
      // Depois da PRIMEIRA tentativa a UI sai do loading — o card mostra
      // status desconhecido enquanto o retry corrige em background. Melhor um
      // provisório que se corrige do que um spinner de 13s.
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
    void (async () => {
      for (let attempt = 0; attempt < REFRESH_RETRY_DELAYS_MS.length; attempt++) {
        if (REFRESH_RETRY_DELAYS_MS[attempt] > 0) await sleep(REFRESH_RETRY_DELAYS_MS[attempt]);
        if (cancelled) return;
        if (await refresh()) return;
        if (cancelled) return;
      }
      console.error(
        "[useWhatsAppLink] status lookup esgotou as tentativas — status desconhecido",
      );
    })();
    return () => { cancelled = true; };
  }, [refresh]);

  const activate = useCallback(async (source: string) => {
    setActivating(true);
    try {
      const { data, error } = await supabase.functions.invoke("whatsapp-link-code", {
        body: { source },
      });
      if (error) throw error;
      const deeplink = (data as { deeplink?: string } | null)?.deeplink;
      if (!deeplink) throw new Error("no_deeplink");
      // Opens WhatsApp (web: full-page nav to wa.me → OS hands off to the app;
      // native: the same wrapper the app uses for external links).
      platformOpen(deeplink);
    } catch (err) {
      console.error("[useWhatsAppLink] activate failed", err);
      throw err;
    } finally {
      setActivating(false);
    }
  }, []);

  const disconnect = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { error } = await supabase
      .from("whatsapp_links")
      .update({ active: false })
      .eq("user_id", user.id);
    if (error) {
      console.error("[useWhatsAppLink] disconnect failed", error);
      throw error;
    }
    setConnected(false);
  }, []);

  return { connected, statusKnown, loading, activating, activate, disconnect, refresh };
}
